import React from "react";
import { totalsByCurrency } from "../../lib/portfolioMetrics";
import { convertToBase, SUPPORTED_BASES } from "../../lib/fx";

function fmt(val) {
  if (val === null || val === undefined) return "-";
  return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function gainClass(val) {
  if (val === null || val === undefined || val === 0) return "";
  return val > 0 ? "text-success" : "text-danger";
}

function CardLine({ label, value, className, strong }) {
  return (
    <div className="d-flex justify-content-between">
      <span className="text-muted">{label}</span>
      {strong
        ? <strong className={className}>{value}</strong>
        : <span className={className}>{value}</span>}
    </div>
  );
}

function PerformanceCard({ title, lines, returnPct, testId, headerExtra }) {
  return (
    <div className="card mb-3" data-testid={testId}>
      <div className="card-header fw-bold d-flex justify-content-between align-items-center">
        <span>{title}</span>
        {headerExtra}
      </div>
      <div className="card-body py-2">
        {lines.map((l) => <CardLine key={l.label} {...l} />)}
        <hr className="my-1" />
        <CardLine
          label="Total return"
          value={returnPct !== null ? `${fmt(returnPct)}%` : "-"}
          className={gainClass(returnPct)}
          strong
        />
      </div>
    </div>
  );
}

// Sharesight-style performance overview. With FX rates loaded everything is
// converted into the selected base currency (single card, including the
// currency-gain component); otherwise totals are grouped per currency.
export function PortfolioSummary({ rows, metricsByTicker, fx, base, onBaseChange }) {
  if (!rows || rows.length === 0) return null;

  const selector = onBaseChange && (
    <select
      className="form-select form-select-sm w-auto fw-normal"
      value={base}
      onChange={(e) => onBaseChange(e.target.value)}
      aria-label="Base currency"
      data-testid="base-currency-select"
    >
      {SUPPORTED_BASES.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );

  if (fx) {
    // Rows are already base-converted by buildRows.
    const valued = rows.filter((r) => r.value !== null && r.value !== undefined);
    const value  = valued.reduce((sum, r) => sum + r.value, 0);
    const cost   = valued.reduce((sum, r) => sum + (r.costBasis || 0), 0);
    // Unrealized only — realized gains get their own line below.
    const unrealizedOf = (r) => r.capitalGainUnrealized ?? r.capitalGain;
    const gain   = valued.reduce((sum, r) => sum + (unrealizedOf(r) || 0), 0);
    const income = rows.reduce((sum, r) => sum + (r.income || 0), 0);

    // Currency gain: base-currency capital gain minus the local gain
    // converted at today's rate — the part of the gain caused by FX moves.
    let currencyGain = 0;
    for (const r of valued) {
      const unrealized = unrealizedOf(r);
      if (unrealized === null || unrealized === undefined) continue;
      if (r.capitalGainLocal === null || r.capitalGainLocal === undefined) continue;
      const localAtToday = convertToBase(r.capitalGainLocal, r.currency, fx);
      if (localAtToday !== null) currencyGain += unrealized - localAtToday;
    }

    // Realized gains in account currency where the broker reported cash
    // amounts; falls back to instrument-currency FIFO gains otherwise.
    let realized = 0;
    for (const m of Object.values(metricsByTicker || {})) {
      const converted = m.realizedGainAccount !== null && m.realizedGainAccount !== undefined
        ? convertToBase(m.realizedGainAccount, m.accountCurrency, fx)
        : convertToBase(m.realizedGain, m.currency, fx);
      if (converted !== null) realized += converted;
    }

    const returnPct = cost > 0 ? ((gain + income) / cost) * 100 : null;

    return (
      <div className="row">
        <div className="col-md-6 col-lg-5">
          <PerformanceCard
            title={`Performance (${fx.base})`}
            testId={`summary-card-${fx.base}`}
            headerExtra={selector}
            returnPct={returnPct}
            lines={[
              { label: "Market value", value: fmt(value), strong: true },
              { label: "Cost base", value: fmt(cost) },
              { label: "Capital gain", value: fmt(gain), className: gainClass(gain) },
              { label: "— of which currency gain", value: fmt(currencyGain), className: gainClass(currencyGain) },
              { label: "Realized gain", value: fmt(realized), className: gainClass(realized) },
              { label: "Dividend income", value: fmt(income), className: gainClass(income) },
            ]}
          />
        </div>
      </div>
    );
  }

  // FX rates not loaded (or fetch failed): per-currency cards as a fallback.
  // Capital gain line shows unrealized only; realized has its own line.
  const rowsWithUnrealized = rows.map((r) => ({
    ...r,
    capitalGainUnrealizedEff: r.capitalGainUnrealized ?? r.capitalGain,
  }));
  const valueTotals  = totalsByCurrency(rows, "value");
  const costTotals   = totalsByCurrency(rows, "costBasis");
  const gainTotals   = totalsByCurrency(rowsWithUnrealized, "capitalGainUnrealizedEff");
  const incomeTotals = totalsByCurrency(rows, "income");

  const realizedTotals = {};
  for (const m of Object.values(metricsByTicker || {})) {
    if (!m.currency || m.realizedGain === 0) continue;
    realizedTotals[m.currency] = (realizedTotals[m.currency] || 0) + m.realizedGain;
  }

  const currencies = Object.keys(valueTotals);
  if (currencies.length === 0) return null;

  return (
    <div className="row">
      {currencies.map((cur) => {
        const cost = costTotals[cur];
        const gain = gainTotals[cur];
        const income = incomeTotals[cur];
        const returnPct = cost > 0 && gain !== undefined
          ? ((gain + (income || 0)) / cost) * 100
          : null;
        return (
          <div className="col-md" key={cur}>
            <PerformanceCard
              title={`Performance (${cur})`}
              testId={`summary-card-${cur}`}
              headerExtra={cur === currencies[0] ? selector : null}
              returnPct={returnPct}
              lines={[
                { label: "Market value", value: fmt(valueTotals[cur]), strong: true },
                { label: "Cost base", value: cost !== undefined ? fmt(cost) : "-" },
                { label: "Capital gain", value: gain !== undefined ? fmt(gain) : "-", className: gainClass(gain) },
                { label: "Realized gain", value: realizedTotals[cur] !== undefined ? fmt(realizedTotals[cur]) : "-", className: gainClass(realizedTotals[cur]) },
                { label: "Dividend income", value: income !== undefined ? fmt(income) : "-", className: gainClass(income) },
              ]}
            />
          </div>
        );
      })}
    </div>
  );
}
